import { CART_SHUFFLE_TIMEOUT_ERROR_MESSAGE } from '@hungryroot/rest-hooks';
import { Button, Heading, Paragraph, Whoops } from '@hungryroot/ui-kit';

import styles from './UnknownAppCrash.module.css';

export const COPY = {
  default: {
    title: 'Whoops!',
    body: `Clean up on aisle...well, all of the aisles. Hang tight—we'll look into this issue but in the meantime, you can try refreshing the page or head back to our homepage.`,
  },
  outdatedBrowser: {
    title: 'Oops! Your browser needs an update',
    body: `It looks like your browser is out of date and can't display this page properly. To keep things running smoothly, please update your browser or switch to a different one.`,
  },
  privacySettings: {
    title: 'Oops! Your browser is blocking this page',
    body: `It looks like your browser is blocking this page due to privacy settings. Please check your browser settings and enable cookies, local storage, and session storage.`,
  },
  cartShufflingTimeout: {
    title: "Hang tight, we're filling your cart",
    body: "We're getting your items in order, but it's taking a bit longer than expected. You can refresh the page or head back to the homepage while we finish up.",
  },
};

interface UnknownAppCrashProps {
  errorMessage?: string;
}

export function UnknownAppCrash(props: UnknownAppCrashProps) {
  const { errorMessage } = props;

  let copy = COPY.default;

  // Check if the browser supports the below APIs to determine if the browser is out of date:
  // - Intl.ListFormat
  // - IntersectionObserver
  if (
    typeof Intl?.ListFormat !== 'function' ||
    typeof IntersectionObserver !== 'function'
  ) {
    copy = COPY.outdatedBrowser;
  }

  try {
    localStorage.getItem('local-storage-test');
    sessionStorage.getItem('session-storage-test');
  } catch {
    // This is a privacy setting that is blocking the page from loading.
    copy = COPY.privacySettings;
  }

  // The cart is taking longer than expected to shuffle and we currently don't have a way to
  // let users use our application until the cart is ready.
  if (errorMessage === CART_SHUFFLE_TIMEOUT_ERROR_MESSAGE) {
    copy = COPY.cartShufflingTimeout;
  }

  return (
    <div className={styles.container}>
      <Whoops className={styles.illustration} />
      <div className={styles['text-content']}>
        <Heading variant="H2" className={styles.title}>
          {copy.title}
        </Heading>
        <Paragraph className={styles.body} variant="body">
          {copy.body}
        </Paragraph>
      </div>
      <Button
        prominence="secondary"
        size="medium"
        style="muted"
        buttonType="link"
        to="/"
        textPrimary="Back to home"
      />
    </div>
  );
}
